import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#updateButtonStates();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const min = parseInt(quantityInput.min);
    const max = parseInt(quantityInput.max);
    const newValue = parseInt(quantityInput.value);

    if (Number.isNaN(newValue)) {
      quantityInput.value = Number.isNaN(min) ? '0' : String(min);
      return;
    }

    if (!Number.isNaN(min) && newValue < min) quantityInput.value = String(min);
    if (!Number.isNaN(max) && newValue > max) quantityInput.value = String(max);

    this.#updateButtonStates();
  };

  /**
   * Disables the plus/minus buttons once the input's min/max bound is reached.
   */
  #updateButtonStates = () => {
    const { quantityInput, quantityButtons } = this.refs;
    const buttons = Array.isArray(quantityButtons) ? quantityButtons : quantityButtons ? [quantityButtons] : [];
    if (!quantityInput || buttons.length === 0) return;

    const min = parseInt(quantityInput.min);
    const max = parseInt(quantityInput.max);
    const value = parseInt(quantityInput.value);

    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) continue;

      if (button.name === 'plus') {
        button.disabled = !Number.isNaN(max) && !Number.isNaN(value) && value >= max;
      } else if (button.name === 'minus') {
        button.disabled = !Number.isNaN(min) && !Number.isNaN(value) && value <= min;
      }
    }
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
